// Vitrin AI widget giriş noktası — bağımlılıksız (React yok), Shadow DOM içinde izole çalışır.
// Tek script etiketiyle herhangi bir siteye eklenebilir: <script src="...widget.js" data-store-id="..."></script>
import type { ChannelType, RecommendedProductView } from '@vitrin-ai/shared';
import { WIDGET_STYLES } from './styles.js';
import { sendMessage, trackClick } from './api.js';
import { SPARKLE_ICON, ARROW_UP_ICON, CHEVRON_DOWN_ICON } from './icons.js';

interface WidgetConfig {
  storeId: string;
  apiUrl: string;
  privacyUrl?: string;
}

const CHANNEL: ChannelType = 'widget';
const DEFAULT_API_URL = 'http://localhost:3001';

// Global config: İkas storefront enjeksiyonu gibi widget.js'in <script src> etiketini
// programatik eklediği (document.currentScript'in null olduğu) durumlar için. Bootstrap,
// widget.js'i yüklemeden önce window.__vitrinAI'yi doldurur.
interface VitrinAIGlobal {
  storeId?: string;
  apiUrl?: string;
  privacyUrl?: string;
}

function readConfig(): WidgetConfig | null {
  const script = document.currentScript as HTMLScriptElement | null;
  const g = ((window as unknown as { __vitrinAI?: VitrinAIGlobal }).__vitrinAI ?? {}) as VitrinAIGlobal;

  // Öncelik data-* özniteliklerinde (standalone/moodlab kurulumu), yoksa global config.
  const storeId = script?.dataset.storeId ?? g.storeId;

  if (!storeId) {
    console.warn('[Vitrin AI] store id bulunamadı (data-store-id veya window.__vitrinAI), widget başlatılmadı.');
    return null;
  }

  const apiUrl = (script?.dataset.apiUrl ?? g.apiUrl ?? DEFAULT_API_URL).replace(/\/+$/, '');
  const privacyUrl = script?.dataset.privacyUrl ?? g.privacyUrl;

  return { storeId, apiUrl, privacyUrl };
}

// document.currentScript sadece senkron çalışırken geçerli, bu yüzden config'i
// modül yüklenir yüklenmez (DOMContentLoaded'ı beklemeden) okumamız gerekiyor.
const config = readConfig();

function conversationStorageKey(storeId: string): string {
  return `vitrin-ai-conversation-${storeId}`;
}

function formatPrice(price: number, currency: string): string {
  const symbol = currency === 'TRY' ? 'TL' : currency;
  return `${price.toLocaleString('tr-TR')} ${symbol}`;
}

function mount(activeConfig: WidgetConfig): void {
  const host = document.createElement('div');
  host.id = 'vitrin-ai-widget-root';
  document.body.appendChild(host);
  const shadow = host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = WIDGET_STYLES;
  shadow.appendChild(styleEl);

  const root = document.createElement('div');
  root.className = 'vai-root';
  shadow.appendChild(root);

  // --- Tetikleyici: arama çubuğu görünümlü pill ---
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'vai-trigger';
  trigger.innerHTML = `
    <span class="vai-trigger-icon">${SPARKLE_ICON}</span>
    <span class="vai-trigger-label">Stil danışmanına sor...</span>
    <span class="vai-trigger-send">${ARROW_UP_ICON}</span>
  `;
  root.appendChild(trigger);

  // --- Panel ---
  const panel = document.createElement('div');
  panel.className = 'vai-panel';
  panel.style.display = 'none';
  panel.innerHTML = `
    <div class="vai-header">
      <div class="vai-header-left">
        <span>${SPARKLE_ICON}</span>
        <div class="vai-header-title">Stil Danışmanı</div>
      </div>
      <button type="button" class="vai-close" aria-label="Kapat">${CHEVRON_DOWN_ICON}</button>
    </div>
    <div class="vai-body"></div>
    <div class="vai-footer">
      <div class="vai-input-row">
        <input type="text" class="vai-input" placeholder="Bir şey yaz..." />
        <button type="button" class="vai-send" aria-label="Gönder">${ARROW_UP_ICON}</button>
      </div>
      <div class="vai-legal">
        Yanıtlar yapay zekâ tarafından üretilir ve hatalı olabilir.${
          activeConfig.privacyUrl
            ? ` Kişisel verilerinin işlenmesine ilişkin detaylı bilgiye <a href="${activeConfig.privacyUrl}" target="_blank" rel="noopener">KVKK Aydınlatma Metni</a>'nden ulaşabilirsin.`
            : ''
        }
      </div>
    </div>
  `;
  root.appendChild(panel);

  const body = panel.querySelector('.vai-body') as HTMLDivElement;
  const closeBtn = panel.querySelector('.vai-close') as HTMLButtonElement;
  const input = panel.querySelector('.vai-input') as HTMLInputElement;
  const sendBtn = panel.querySelector('.vai-send') as HTMLButtonElement;

  let conversationId: string | undefined =
    sessionStorage.getItem(conversationStorageKey(activeConfig.storeId)) ?? undefined;
  let hasStartedChat = false;
  let sending = false;

  function showWelcome(): void {
    const welcome = document.createElement('div');
    welcome.className = 'vai-welcome';
    welcome.innerHTML = `
      <div class="vai-welcome-badge">${SPARKLE_ICON}</div>
      <div class="vai-welcome-heading">Hoş Geldin!</div>
      <div class="vai-capabilities">
        <div class="vai-capability">Ürün Önerileri</div>
        <div class="vai-capability">Kombin Rehberliği</div>
        <div class="vai-capability">Beden Tavsiyesi</div>
      </div>
      <div class="vai-welcome-prompt">Nereden başlayalım?</div>
    `;
    body.appendChild(welcome);
  }

  function clearWelcome(): void {
    const welcome = body.querySelector('.vai-welcome');
    welcome?.remove();
  }

  function scrollToBottom(): void {
    body.scrollTop = body.scrollHeight;
  }

  function addUserMessage(text: string): void {
    const el = document.createElement('div');
    el.className = 'vai-message vai-message-user';
    el.textContent = text;
    body.appendChild(el);
    scrollToBottom();
  }

  function buildProductCard(product: RecommendedProductView): HTMLDivElement {
    const card = document.createElement('div');
    card.className = 'vai-product-card';

    // Görseli olmayan ürünlerde boş src kırık resim ikonu gösteriyordu; kartı görselsiz çiz.
    if (product.imageUrl) {
      const img = document.createElement('img');
      img.className = 'vai-product-image';
      img.src = product.imageUrl;
      img.alt = product.name;
      img.loading = 'lazy';
      card.appendChild(img);
    }

    const name = document.createElement('div');
    name.className = 'vai-product-name';
    name.textContent = product.name;
    card.appendChild(name);

    const price = document.createElement('div');
    price.className = 'vai-product-price';
    price.textContent = formatPrice(product.price, product.currency);
    card.appendChild(price);

    const reason = document.createElement('div');
    reason.className = 'vai-product-reason';
    reason.textContent = product.reason;
    card.appendChild(reason);

    const link = document.createElement('a');
    link.className = 'vai-product-link';
    link.href = product.productUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = 'Ürüne Git';
    link.addEventListener('click', () => {
      if (conversationId) trackClick(activeConfig.apiUrl, conversationId, product.productId);
    });
    card.appendChild(link);

    return card;
  }

  function addAssistantMessage(text: string, products: RecommendedProductView[]): void {
    const el = document.createElement('div');
    el.className = 'vai-message vai-message-assistant';
    el.textContent = text;
    body.appendChild(el);

    if (products.length > 0) {
      const list = document.createElement('div');
      list.className = 'vai-products';
      for (const product of products) {
        list.appendChild(buildProductCard(product));
      }
      body.appendChild(list);
    }

    scrollToBottom();
  }

  function showTyping(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'vai-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    body.appendChild(el);
    scrollToBottom();
    return el;
  }

  async function handleSend(): Promise<void> {
    const text = input.value.trim();
    if (!text || sending) return;

    if (!hasStartedChat) {
      clearWelcome();
      hasStartedChat = true;
    }

    input.value = '';
    addUserMessage(text);

    sending = true;
    sendBtn.disabled = true;
    const typingEl = showTyping();

    try {
      const response = await sendMessage({
        apiUrl: activeConfig.apiUrl,
        storeId: activeConfig.storeId,
        conversationId,
        message: text,
        channel: CHANNEL,
      });

      conversationId = response.conversationId;
      sessionStorage.setItem(conversationStorageKey(activeConfig.storeId), conversationId);

      typingEl.remove();
      addAssistantMessage(response.replyText, response.recommendedProducts);
    } catch (err) {
      typingEl.remove();
      addAssistantMessage('Bir şeyler ters gitti, birazdan tekrar dener misin?', []);
      console.error('[Vitrin AI]', err);
    } finally {
      sending = false;
      sendBtn.disabled = false;
      input.focus();
    }
  }

  function openPanel(): void {
    panel.style.display = 'flex';
    trigger.style.display = 'none';
    if (body.children.length === 0) {
      showWelcome();
    }
    input.focus();
  }

  function closePanel(): void {
    panel.style.display = 'none';
    trigger.style.display = 'flex';
  }

  trigger.addEventListener('click', openPanel);
  closeBtn.addEventListener('click', closePanel);
  sendBtn.addEventListener('click', () => void handleSend());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void handleSend();
  });
}

if (config) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(config));
  } else {
    mount(config);
  }
}
